import React, { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { createEnquiry } from "@/lib/api";
import { Toaster, toast } from "sonner";

const initial = {
 name: "",
 email: "",
 phone: "",
 business_type: "",
 message: "",
};

export const EnquiryForm = ({ compact = false, sourcePage = "unknown" }) => {
 const [form, setForm] = useState(initial);
 const [busy, setBusy] = useState(false);
 const [done, setDone] = useState(false);

 const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

 const submit = async (e) => {
 e.preventDefault();
 if (busy) return;
 if (!form.name || !form.email || !form.phone) {
 toast.error("Please share your name, email and phone so we can reach you.");
 return;
 }
 setBusy(true);
 try {
 await createEnquiry({ ...form, source_page: sourcePage });
 setDone(true);
 setForm(initial);
 toast.success("We've received your enquiry. Bhavik or the team will reach out shortly.");
 } catch (err) {
 toast.error("Something went wrong. Please try again or WhatsApp us.");
 } finally {
 setBusy(false);
 }
 };

 const input = "w-full bg-[#F8F9FA] border border-[#EAEAEA] rounded-lg px-4 py-3 text-[15px] text-[#0F1115] focus:outline-none focus:border-[#FDD805] focus:ring-1 focus:ring-[#FDD805] transition-colors placeholder:text-[#8a8a8a]";

 if (done) {
 return (
 <div className="border border-[#EAEAEA] rounded-2xl p-8 bg-white text-center" data-testid="enquiry-success">
 <div className="w-14 h-14 rounded-full bg-[#FFF9E6] mx-auto flex items-center justify-center">
 <CheckCircle2 className="text-[#0F1115]" />
 </div>
 <h3 className="mt-4 font-display text-2xl font-black">Enquiry received.</h3>
 <p className="mt-2 text-[#1C1F26]">
 Thank you. We'll reach out on WhatsApp within 24 hours to schedule a
 free 15-min consultation.
 </p>
 <button
 onClick={() => setDone(false)}
 className="mt-6 bg-white text-[#0F1115] border-2 border-[#0F1115] rounded-full px-6 py-2 font-bold text-sm hover:bg-[#0F1115] hover:text-white transition-colors"
 data-testid="enquiry-send-another"
 >
 Send another enquiry
 </button>
 </div>
 );
 }

 return (
 <form
 onSubmit={submit}
 className={`bg-white border border-[#EAEAEA] rounded-2xl ${compact ? "p-6" : "p-8"} space-y-4`}
 data-testid="enquiry-form"
 >
 <Toaster richColors position="top-center" />
 {!compact && (
 <>
 <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#4A4A4A]">
 Free 15-min consultation
 </div>
 <h3 className="font-display text-2xl md:text-3xl font-black text-[#0F1115]">
 Tell us about your business.
 </h3>
 <p className="text-[#1C1F26] text-[15px]">
 No pushy sales calls. Just a real conversation to understand where
 you're stuck.
 </p>
 </>
 )}

 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <input
 className={input}
 placeholder="Full name"
 value={form.name}
 onChange={upd("name")}
 data-testid="enquiry-input-name"
 required
 />
 <input
 className={input}
 placeholder="Email"
 type="email"
 value={form.email}
 onChange={upd("email")}
 data-testid="enquiry-input-email"
 required
 />
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <input
 className={input}
 placeholder="Phone / WhatsApp"
 value={form.phone}
 onChange={upd("phone")}
 data-testid="enquiry-input-phone"
 required
 />
 <select
 className={input}
 value={form.business_type}
 onChange={upd("business_type")}
 data-testid="enquiry-input-business-type"
 >
 <option value="">I'm looking for…</option>
 <option>Private Limited Registration</option>
 <option>LLP Registration</option>
 <option>OPC Registration</option>
 <option>Startup India / DPIIT</option>
 <option>GST / MSME / Compliance</option>
 <option>Business Presence Setup</option>
 <option>Not sure yet need guidance</option>
 </select>
 </div>
 <textarea
 className={`${input} min-h-[110px] resize-y`}
 placeholder="Where are you in your founder journey? (optional)"
 value={form.message}
 onChange={upd("message")}
 data-testid="enquiry-input-message"
 />

 <button
 type="submit"
 disabled={busy}
 className="w-full bg-[#0F1115] text-white rounded-full px-6 py-3.5 font-bold text-[15px] hover:bg-[#1c1f26] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-70"
 data-testid="enquiry-submit-button"
 >
 {busy ? <Loader2 className="animate-spin" size={18} /> : null}
 {busy ? "Sending…" : "Submit Enquiry"}
 </button>

 <p className="text-[12px] text-[#4A4A4A] text-center">
 By submitting you agree to be contacted about your enquiry.
 </p>
 </form>
 );
};

export default EnquiryForm;
